import type { ValuesOf } from '@microsoft/fast-foundation';

import { css } from '@microsoft/fast-element';
import { LayerBaseLuminance, focusStrokeOuter, layerFillBaseLuminance } from '@adaptive-web/adaptive-ui/reference';
import { SwatchRGB } from '@adaptive-web/adaptive-ui';

export const SiteColors = {
	brandBlue: '#4b85c3',
	brandGreen: '#91cc94',
	darkBlue: '#315882',
	lightBlue: '#a5c2e1',
	neutral: '#f0f1f4'
} as const;

export type SiteColors = ValuesOf<typeof SiteColors>;

export const lightPalette = {
  primary: "#22303D",
  secondary: "#0AED8B",
  surface: "#E0E1E2",
  background: "#FFFFFF",
  onPrimary: "#FFFFFF",
  onSecondary: "#22303D",
  onSurface: "#22303D",
  onBackground: "#22303D",
	text: "#22303D"
} as const;

export const darkPalette = {
  primary: "#22303D",
  secondary: "#0AED8B",
  surface: "#3C546B",
  background: "#22303D",
  onPrimary: "#FFFFFF",
  onSecondary: "#22303D",
  onSurface: "#537494",
  onBackground: "#22303D",
	text: "#FFFFFF"
} as const;

export const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
export const lightModeQuery = window.matchMedia('(prefers-color-scheme: light)');
export const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
export const mobileQuery = window.matchMedia('(max-width: 659px)');

/**
 * Sets light/dark mode for native UI elements like scrollbars.
 */
export const darkStyles = css`
	:root {
		color-scheme: dark;
		--primary-color: ${darkPalette.primary};
		--secondary-color: ${darkPalette.secondary};
		--background-color: ${darkPalette.background};
		--surface-color: ${darkPalette.surface};
		--on-surface-color: ${darkPalette.onSurface};
		--on-primary-color: ${darkPalette.onPrimary};
		--on-secondary-color: ${darkPalette.onSecondary};
		--section-title-color: #BDDEFF;
		--text-color: ${darkPalette.text};
	}
`;

export const lightStyles = css`
	:root {
		color-scheme: light;
		--primary-color: ${lightPalette.primary};
		--secondary-color: ${lightPalette.secondary};
		--background-color: ${lightPalette.background};
		--surface-color: ${lightPalette.surface};
		--on-primary-color: ${lightPalette.onPrimary};
		--on-secondary-color: ${lightPalette.onSecondary};
		--section-title-color: #83BBE5;
		--text-color: ${lightPalette.text};
	}
`;

export function setColorScheme(event: MediaQueryListEvent | MediaQueryList): void {
	if (event.matches) {
		darkStyles.addStylesTo(document);
		lightStyles.removeStylesFrom(document);
		
		layerFillBaseLuminance.withDefault(LayerBaseLuminance.DarkMode);
	} else {
		lightStyles.addStylesTo(document);
		darkStyles.removeStylesFrom(document);
		focusStrokeOuter.withDefault(SwatchRGB.from({ r: 0.13, g: 0.18, b: 0.23}));
		
		layerFillBaseLuminance.withDefault(LayerBaseLuminance.LightMode);
	}
}

export function addMediaListeners(): void {
	darkModeQuery.addEventListener('change', setColorScheme);
}

export function removeMediaListeners(): void {
	darkModeQuery.removeEventListener('change', setColorScheme);
}